import React, { useState, useRef } from "react";

import "./contentpage.css";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
function IslamicTradePage10() {
  const contentRef = useRef(null);
  const links = {
    prev: "/islamic-trade-page-9",
    index: "/index",
    next: "/islamic-trade-page-11",
  };
  const titleText = "Rulings concerning Loan (Qardh)";
  const questions = [
    {
      text: "1. What is the ruling of giving a loan (Qardh) in Islam for the lender?",
      options: [
        {
          value: "A",
          label: "A. Obligatory in all cases.",
        },
        {
          value: "B",
          label: "B. Makruh (discouraged).",
        },
        {
          value: "C",
          label:
            "C. Mandoob (recommended) and possibly Fard in cases of dire need.",
        },
        {
          value: "D",
          label: "D. Haram (forbidden).",
        },
      ],
      correct: "C",
    },
    {
      text: "2. According to Islamic teachings, when is it permissible for a lender to demand repayment of a loan?",
      options: [
        { value: "A", label: "A. At any time, regardless of the agreement." },
        {
          value: "B",
          label: "B. Before the agreed term if the lender needs the money",
        },
        {
          value: "C",
          label:
            "C. Only after the time period stipulated in the loan agreement.",
        },
        { value: "D", label: "D. Whenever the borrower is able to repay" },
      ],
      correct: "C",
    },
    {
      text: "3. What is the Islamic stance on receiving additional benefits in return for a loan?",
      options: [
        { value: "A", label: "A. Allowed if agreed by both parties" },
        { value: "B", label: "B. Permissible only in business loans" },
        { value: "C", label: "C. Encouraged as a gesture of appreciation" },
        {
          value: "D",
          label: "D. Prohibited as it constitutes riba (interest)",
        },
      ],
      correct: "D",
    },
    {
      text: "4. What happens to the ownership of a loan once it is given in Islam?",
      options: [
        {
          value: "A",
          label: "A. It remains shared between lender and borrower",
        },
        {
          value: "B",
          label: "B. The lender retains partial rights over its usage",
        },
        {
          value: "C",
          label:
            "C. The borrower becomes the full owner and can use it as he wishes",
        },
        { value: "D", label: "D. It must be returned before any use" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container" ref={contentRef}>
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={10}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content">
        Welcome to <b>Day 10</b>! Today, we will explore one of the most
        spiritually rewarding aspects of Islamic financial ethics i.e Loans
        (Qardh). Loans (Qardh) occupy a deeply compassionate place in the moral
        and financial framework of Islam. In the Islamic worldview, money is not
        seen as a commodity to generate profit from itself, but as a means to
        facilitate beneficial exchange, cooperation, and community welfare. When
        one gives a loan in Islam, it is not seen as an act of commerce but as
        an act of charity, mercy, and solidarity. The very term Qardh Hasan i.e
        a “beautiful loan” embodies the spirit of kindness, as the act is done
        purely for the sake of Allah, with no worldly return expected.
        <br />
        In contrast to the conventional economic systems that prioritize gain
        and interest, Islam positions Qardh within the realm of moral duty.
        Lending to someone in distress is an act of relieving them from
        hardship, enabling them to stand on their feet again. The Qur’an and the
        Sunnah repeatedly call on believers to help one another, particularly
        those who are struggling financially. A Qardh is a lifeline extended to
        another believer in the hope that Allah will extend His mercy and
        blessings in return.
        <br />
        The concept of Qardh is founded on the principles of Ihsan (excellence
        in conduct) and Takaful (mutual responsibility). Through lending, wealth
        circulates in society, ensuring that it does not remain hoarded in the
        hands of a few. This circulation maintains social balance and promotes a
        sense of belonging among Muslims. It also curtails envy and resentment
        that often arise from economic disparity. The Prophet Muhammad (peace be
        upon him) encouraged believers to think of wealth as a trust from Allah,
        to be used for benefit, not oppression. When a person lends to another
        without charging interest, he is in effect using that trust to please
        Allah and uplift others.
        <br />
        In pre-Islamic Arabia, lending was often associated with exploitation.
        The rich would lend to the poor and charge high interest rates, trapping
        them in cycles of debt. Islam abolished this oppressive system entirely.
        Through the prohibition of riba (interest), Islam restored dignity to
        both parties in a financial transaction. Lending became a moral act
        rather than a profit-seeking enterprise. A loan in Islam is meant to
        create ease, not burden; to heal, not harm.
        <br />
        Allah commands in the Qur’an, “If the debtor is in difficulty, then
        grant him time until it is easy for him to repay. But if you remit it by
        way of charity, that is better for you if you only knew.”{" "}
        <a href="https://quran.com/2/280" target="blank">
          (Surah Al-Baqarah 2:280)
        </a>
        <br />
        This verse not only instructs patience and leniency toward the debtor
        but also elevates the act of forgiving debt to the level of charity. The
        lender gains immense spiritual reward by choosing mercy over material
        recovery. This is why many scholars have described lending as an act
        that brings the lender closer to Allah, for it reflects divine
        attributes of compassion and forgiveness.
        <br />
        The Prophet Muhammad (peace be upon him) also demonstrated through his
        personal conduct how important it is to treat borrowers with kindness.
        He would personally forgive debts of those who were genuinely unable to
        pay and would remind his companions to do the same. The act of forgiving
        or giving respite to the indebted is a way of participating in Allah’s
        mercy. In this sense, Qardh becomes more than a financial instrument, it
        becomes a spiritual pathway to the pleasure of Allah.
        <br />
        Through Qardh, Islam aims to purify the heart from greed and
        self-centeredness. It redirects human behavior toward altruism and
        cooperation. The lender gains no worldly benefit but earns the promise
        of divine reward. The borrower, in turn, gains dignity, stability, and
        an opportunity to rebuild. Together, both parties participate in an
        exchange that benefits the entire society.
        <br /> <br />
        <b>Legal and Ethical Rulings on Qardh</b>
        <br />
        <br />
        In Islamic jurisprudence (fiqh), Qardh holds a unique place among
        financial contracts. Unlike trade (bay‘), which is built upon profit and
        exchange of value, Qardh is purely charitable in nature. The rulings
        surrounding it ensure that the ethical spirit of the act is not
        compromised by self-interest or exploitation.
        <br />
        Offering a loan is considered mandoob (recommended) for the lender. It
        becomes fard (obligatory) in situations where the borrower is in genuine
        hardship and the lender possesses the means to help. This ruling
        reflects the social responsibility that Islam imposes on those blessed
        with wealth. The lender’s surplus is not to be hoarded but to be used
        for communal good. For the borrower, taking a loan is mubah
        (permissible) provided he has the capacity and intention to repay.
        Borrowing without intention or ability to repay would constitute
        injustice, which is forbidden.
        <br />
        Islamic law clearly differentiates between loans and commercial
        activities. The goal of trade is profit; the goal of lending is relief.
        Therefore, it is prohibited to derive any material gain or benefit from
        a loan. If the lender demands extra payment, gifts, favors, or any form
        of advantage in return for lending, it constitutes riba, which is
        categorically forbidden.
        <br />
        A Qardh becomes binding once the borrower takes possession of the loaned
        asset. From that moment, he becomes its owner, and any use or profit
        arising from it belongs to him alone. For example, if a man borrows one
        bitcoin, he becomes its rightful owner and may use it as he wishes. The
        lender cannot demand a share of the profit if the borrower invests it
        successfully, unless a separate, lawful partnership contract is formed.
        Likewise, if the borrower suffers a loss, the borrower cannot claim
        compensation and is liable to return the principal amount lent.
        <br />
        Another important rule in Qardh is the respect of the stipulated
        repayment period. If a specific duration has been agreed upon, the
        lender has no right to demand repayment before the term expires. For
        instance, if a loan was given for one year, it is impermissible to seek
        repayment before that period ends. Similarly, if the borrower is
        genuinely struggling, it is commendable, and sometimes obligatory, for
        the lender to grant an extension. Pressuring or humiliating a debtor is
        considered sinful.
        <br />
        The Prophet (peace be upon him) said,: "May Allah's mercy be on him who
        is lenient in his buying, selling, and in demanding back his money."
        <a href="https://sunnah.com/bukhari:2076" target="blank">
          (Sahih Al Bukhari 2076)
        </a>
        <br />
        The principle “whatever is permissible to be sold is permissible to be
        lent” provides further clarity. Items that are haram to sell, such as
        intoxicants or animals forbidden for trade are also impermissible to
        lend. Conversely, items lawful for sale, like vehicles, gold, or money,
        may be lent. This rule ensures that lending remains within the ethical
        and lawful boundaries of Islamic commerce.
        <br />
        Furthermore, Islam safeguards the dignity of the borrower. It is highly
        discouraged to remind the borrower of one’s favor or to use the loan as
        a means of gaining influence or control. Such behavior nullifies the
        spiritual reward of the act.
        <br />
        The Qur’an warns, “O believers! Do not waste your charity with reminders
        ˹of your generosity˺ or hurtful words, like those who donate their
        wealth just to show off and do not believe in Allah or the Last Day.
        Their example is that of a hard barren rock covered with a thin layer of
        soil hit by a strong rain—leaving it just a bare stone. Such people are
        unable to preserve the reward of their charity. Allah does not guide
        ˹such˺ disbelieving people.”{" "}
        <a href="https://quran.com/2/264" target="blank">
          (Surah Al-Baqarah 2:264)
        </a>
        <br />
        Thus, a loan should be given quietly, without public display or
        expectation of recognition.
        <br />
        In practical terms, documenting a loan is recommended to prevent future
        disputes. <br />
        The Qur’an itself commands that debts should be recorded and witnessed:
        <br />
        "O believers! When you contract a loan for a fixed period of time,
        commit it to writing."
        <a href="https://quran.com/2/282" target="blank">
          (Surah Al Baqarah 282)
        </a>{" "}
        <br /> This is not a sign of mistrust but a measure of transparency and
        justice. Written contracts protect both parties and preserve the
        integrity of the transaction.
        <br />
        <br />
        <b>The Moral Dimensions and Spiritual Rewards of Lending</b>
        <br />
        <br />
        The moral weight of Qardh extends far beyond its financial implications.
        It is a test of sincerity, compassion, and trustworthiness. When a
        person lends without hope of return beyond the principal, his heart is
        purified from greed and self-interest. In doing so, he reflects the
        attributes of mercy, generosity, and empathy that Allah loves. Lending
        is an opportunity to serve humanity for the sake of the Creator.
        <br />
        The Qur’an equates lending to Allah Himself. “Who is it that will lend
        Allah a goodly loan, so He may multiply it for him many times over?”
        <a href="https://quran.com/2/245" target="blank">
          (Surah Al-Baqarah 2:245)
        </a>
        <br />
        This powerful metaphor transforms an ordinary act into a spiritual
        investment. The one who gives a Qardh is, in essence, giving to Allah,
        who promises abundant reward. No worldly interest could ever equal this
        divine multiplication.
        <br />
        The Prophet Muhammad (peace be upon him) reinforced this message through
        numerous sayings. In one narration, he said,
        <br /> “He who alleviates the suffering of a brother out of the
        sufferings of the world, Allah would alleviate his suffering from the
        sufferings of the Day of Resurrection, and he who finds relief for one
        who is hard-pressed, Allah would make things easy for him in the
        Hereafter, and he who conceals (the faults) of a Muslim, Allah would
        conceal his faults in the world and in the Hereafter”
        <a href="https://sunnah.com/muslim:2699a" target="blank">
          (Sahih Muslim 2699)
        </a>{" "}
        <br />
        Lending, therefore, becomes a means of securing divine assistance in
        one’s own time of need. It also strengthens social bonds by cultivating
        trust and goodwill among Muslims.
        <br />
        The Prophet (peace be upon him) further said:
        <br />
        "There is no Muslim who lends something to another Muslim twice, but it
        will be like giving charity once"
        <a href="https://sunnah.com/ibnmajah:2430" target="blank">
          (Sunan Ibn Majah 2430)
        </a>
        <br />
        This means that even though a loan is to be repaid, the intention behind
        it is as noble as giving in charity. It bridges the gap between wealth
        and need, fostering a cycle of care in the community. In another
        narration, he praised those who repay loans honorably: <br />
        “The best of you - or among the best of you - are those who pay off
        their debts in the best manner.”{" "}
        <a href="https://sunnah.com/ibnmajah:2423" target="blank">
          (Sunan Ibn Majah 2417)
        </a>
        <br /> Thus, both the lender and the borrower are rewarded for
        fulfilling their moral roles, one through generosity, the other through
        integrity.
        <br />
        Islamic scholars have also emphasized that the act of forgiving or
        reducing a debt carries enormous reward. When a debtor is in hardship,
        pardoning the amount or part of it is a manifestation of true
        compassion. The Prophet (peace be upon him) narrated a story of a man
        who used to forgive the debts of those in difficulty. Allah forgave his
        sins in return and admitted him to Paradise. He (peace be upon him)
        said: <br /> "There was a merchant who used to lend the people, and
        whenever his debtor was in straitened circumstances, he would say to his
        employees, 'Forgive him so that Allah may forgive us.' So, Allah forgave
        him."
        <a href="https://sunnah.com/bukhari:2078" target="blank">
          (Sahih Al-Bukhari 2078)
        </a>
        <br />
        Qardh, therefore, plays a dual role. It alleviates economic distress and
        purifies spiritual character. It reminds the lender that wealth is
        transient and that true profit lies in the hereafter. It reminds the
        borrower of responsibility and gratitude. Through this mutual exchange,
        hearts are softened, and communities become more cohesive.
        <br />
        Even in the economic sense, Qardh contributes to stability. It prevents
        the accumulation of wealth among the few and stimulates consumption and
        production without the burden of interest. In an interest-based system,
        debt becomes a tool of oppression, transferring wealth from the poor to
        the rich. In an Islamic system, debt becomes a tool of empowerment,
        enabling the poor to recover and thrive. <br />
        Islamic ethics further discourage exploiting the vulnerability of
        debtors. Coercion, threats, or humiliation of debtors are grave sins.
        Instead, Islam calls for empathy and patience.
        <br />
        The Prophet (peace be upon him) said : "Whoever grants respite to an
        indigent or alleviates it for him, Allah will shade him on the Day of
        Judgement under His Throne, a Day in which there is no shade except His
        shade."
        <a href="https://sunnah.com/tirmidhi:1306" target="blank">
          (Jami At-Tirmidhi 1306)
        </a>
        <br />
        The lender who forgives or grants time is thus promised protection on
        the most fearful day.
        <br />
        <br />
        <b>The Broader Social Role of Qardh and Its Relevance Today</b>
        <br />
        <br />
        Qardh in Islam is not merely an individual act of kindness, it is a
        pillar of social justice and collective responsibility. When practiced
        widely, it creates a compassionate financial ecosystem that stands in
        contrast to exploitative models. It integrates morality into economics,
        ensuring that financial interactions strengthen society rather than
        divide it.
        <br />
        The Islamic economic model balances two essential dimensions: productive
        enterprise and social welfare. Trade and investment stimulate growth,
        while charitable acts like Qardh ensure equitable distribution. This
        balance prevents extreme inequality. It reflects the Qur’anic ideal of
        moderation: <br />
        “so that wealth may not merely circulate among your rich.”{" "}
        <a href="https://quran.com/59/7" target="blank">
          (Surah Al-Hashr 59:7)
        </a>
        <br />
        In this framework, loans function as redistributive mechanisms that
        uphold social harmony.
        <br />
        Throughout Islamic history, Qardh has played a vital role in community
        development. Wealthy Muslims would lend to farmers, craftsmen, and
        merchants to support their livelihoods without seeking profit. Muslim
        rulers maintained funds known as Bayt al-Mal to provide Qardh Hasan to
        the needy. Such initiatives reduced dependency and nurtured
        self-reliance among the poor. The goal was not mere survival but
        empowerment. By eliminating interest, Islam ensured that economic
        assistance would not become a new form of enslavement.
        <br />
        In the modern world, this concept retains great relevance. Today’s
        global economy is burdened by interest-based debt that traps individuals
        and nations alike. Credit cards, payday loans, and bank loans often
        carry high interest rates, causing long-term hardship. The Islamic
        concept of Qardh offers an ethical alternative that emphasizes
        compassion over profit. It can inspire new models of ethical finance,
        micro-lending, and community-based assistance programs. Islamic
        financial institutions today can maintain Qardh Hasan funds for
        educational or medical support. These initiatives revive the prophetic
        tradition of financial mercy.
        <br />
        Qardh also serves as a moral reminder to those with wealth. It teaches
        that ownership is a trust, not absolute possession. When one gives from
        his surplus, he fulfills the duty of gratitude toward Allah. The wealth
        one shares through Qardh is never truly lost; it returns in blessings,
        contentment, and divine reward. The Qur’an repeatedly assures that
        generosity never decreases wealth. Rather, it multiplies it spiritually:
        <br />
        “If you lend to Allah a good loan, He will multiply it for you and
        forgive you. For Allah is Most Appreciative, Most Forbearing.”{" "}
        <a href="https://quran.com/64/17" target="blank">
          (Surah At-Taghabun 64:17)
        </a>
        <br />
        Beyond individual acts, societies can embed the principles of Qardh into
        their structures. Muslim rulers and communities can establish
        interest-free lending institutions that reflect these values.
        Educational curicullum can revive awareness of Qardh as a tool of faith,
        not merely finance. When Muslims practice Qardh collectively, they
        recreate the sense of brotherhood and moral economy envisioned by Islam.
        <br />
        At the personal level, every Muslim can participate in this system.
        Lending small amounts to friends, family, or neighbors in difficulty is
        a means of living the Sunnah. Even if one cannot give money, offering
        advice, support, or advocacy for those in debt is a form of moral
        participation. The Prophet (peace be upon him) said, <br />
        “The most beloved of people to Allah are those who bring the most
        benefit to others.” (Al-Mu’jam al-Awsaṭ lil-Ṭabarānī 6026)
        <br /> Lending, forgiving, or even delaying repayment, all are acts that
        bring benefit and reflect the mercy of Islam.
        <br />
        In conclusion, Qardh is not a mere economic mechanism but a
        manifestation of faith. It teaches empathy, patience, and humility. It
        transforms wealth into a means of spiritual growth and community
        cohesion. Through Qardh, Islam presents a vision of a world where
        financial power serves moral purpose, where wealth uplifts rather than
        oppresses, and where generosity replaces greed.
        <br />
        When practiced with sincerity, Qardh aligns believers with the divine
        promise of mercy and abundance. It restores balance between material
        life and spiritual purpose, between wealth and welfare, and between
        individual success and collective good. As such, Qardh remains one of
        the most beautiful reflections of Islamic social justice, an enduring
        reminder that faith and finance in Islam are inseparable, each guiding
        the other toward righteousness, compassion, and everlasting reward.
        <br />
        <br /> <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today, we learned that Qardh, or loans in Islam, are acts of benevolence
        intended to support those in financial need without seeking any material
        gain. Islam encourages lending as a form of charity and social
        cooperation, with immense rewards promised in both the Qur'an and
        Sunnah. We understood that while giving a loan is highly recommended for
        the lender, seeking a loan is permissible for the borrower if they have
        the means to repay. We also saw that Islamic law strictly prohibits any
        form of interest or benefit on loans, ensuring that lending remains an
        ethical and compassionate act. Clear rulings govern when repayment can
        be sought, and we learned that the loanee becomes the full owner of the
        loaned asset, with freedom to use or dispose of it. Importantly, we
        discussed the principle that anything impermissible to sell is also
        impermissible to lend. This comprehensive framework upholds fairness,
        protects the dignity of the borrower, and aligns financial dealings with
        Islamic values of justice, generosity, and social upliftment.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br /> Tomorrow we will explore the different types of items that can be
        loaned in Islamic finance and how inflation and market changes impact
        their repayment. We will distinguish between fungible (mithliyyat) and
        non-fungible (mutaqawwimat) items, and learn how Islamic law treats each
        when it comes to returning the borrowed item or its value. You will also
        gain insight into how Islam deals with scenarios like unavailability of
        goods, currency abolishment, and exponential price hikes. Finally, we
        will reinforce the critical Islamic principle that prohibits any form of
        benefit or gain for the lender beyond the original amount, safeguarding
        the loan contract as a pure act of mutual assistance and goodwill.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}
      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicTradePage10;
